import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import useAuth from "../../../hooks/useAuth";
import BaseLayout from "../../../layouts/BaseLayout";
import { BASE_URL } from "../../../utils/Element";

const ScoreCardHistory = () => {
    const [config] = useAuth();
    const [selectedPeriod, setSelectedPeriod] = useState("");
    const [currentGoals, setCurrentGoals] = useState({});
    const [historicalData, setHistoricalData] = useState({ left: [], right: [] });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Generate quarters: last 4 quarters (including current) + next 2 future quarters
    const getAvailableQuarters = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth(); // 0–11
        const currentQuarter = Math.ceil((currentMonth + 1) / 3); // 1–4

        const quarters = [];

        // 2 future quarters (farthest first)
        let year = currentYear;
        let quarter = currentQuarter;

        for (let i = 0; i < 2; i++) {
            quarter += 1;
            if (quarter > 4) {
                quarter = 1;
                year += 1;
            }
        }

        // push farthest future → nearest future
        let fy = year;
        let fq = quarter;

        for (let i = 0; i < 2; i++) {
            quarters.push(`${fy} Q${fq}`);
            fq -= 1;
            if (fq < 1) {
                fq = 4;
                fy -= 1;
            }
        }

        // current quarter
        quarters.push(`${currentYear} Q${currentQuarter}`);

        // 1 past quarter
        let py = currentYear;
        let pq = currentQuarter - 1;
        if (pq < 1) {
            pq = 4;
            py -= 1;
        }
        quarters.push(`${py} Q${pq}`);

        return quarters;
    };


    const periods = getAvailableQuarters();

    // Set default to current quarter (2026 Q1) on mount
    useEffect(() => {
        setSelectedPeriod("2026 Q1");
    }, []);

    const leftGoals = [
        "Dials",
        "Conversations",
        "Talk Time",
        "New 8x8s",
        "New Buyer Leads",
        "Initial Offers w/ Different Buyers",
        "New Seller Leads",
        "New BOVs",
        "Initial Offers w/ Different Sellers",
        "Exclusive Listings",
        "Executed LOI",
        "Gross Hopper Gains",
        "Gross Commission Closed",
    ];

    const goalMap = {
        "Dials": "dials_goal",
        "Conversations": "conversations_goal",
        "Talk Time": "talk_time_goal",
        "New 8x8s": "new_8x8s_goal",
        "New Buyer Leads": "new_buyer_leads_goal",
        "Initial Offers w/ Different Buyers": "initial_offers_w_different_buyers_goal",
        "New Seller Leads": "new_seller_leads_goal",
        "New BOVs": "new_bovs_goal",
        "Initial Offers w/ Different Sellers": "initial_offers_w_different_sellers_goal",
        "Exclusive Listings": "exclusive_listings_goal",
        "Executed LOI": "executed_loi_goal",
        "Gross Hopper Gains": "gross_hopper_gains_goal",
        "Gross Commission Closed": "gross_commission_closed_goal",
    };

    const actualMap = {
        "Dials": "dial",
        "Conversations": "conversation",
        "Talk Time": "talk_time",
        "New 8x8s": "new_8x8",
        "New Buyer Leads": "new_buyer_lead",
        "Initial Offers w/ Different Buyers": "initial_offers_different_buyers",
        "New Seller Leads": "new_seller_lead",
        "New BOVs": "new_bov",
        "Initial Offers w/ Different Sellers": "initial_offers_different_seller",
        "Exclusive Listings": "exclusive_listing",
        "Executed LOI": "executed_loi",
        "Gross Hopper Gains": "gross_hopping_gain",
        "Gross Commission Closed": "gross_hopping_cross",
    };

    const leftGoalTypes = [
        "Dials",
        "Conversations",
        "Talk Time",
        "New 8x8s",
        "New Buyer Leads",
        "Initial Offers w/ Different Buyers",
        "New Seller Leads",
    ];

    const rightGoalTypes = [
        "New BOVs",
        "Initial Offers w/ Different Sellers",
        "Exclusive Listings",
        "Executed LOI",
        "Gross Hopper Gains",
        "Gross Commission Closed",
    ];

    const moneyFields = [
        "Executed LOI",
        "Gross Hopper Gains",
        "Gross Commission Closed",
    ];

    // Break long goal names into two lines after 2 words
    const formatType = (text) => {
        const words = text.split(" ");
        if (words.length <= 2) return text;
        return words.slice(0, 2).join(" ") + "\n" + words.slice(2).join(" ");
    };

    const fetchGoals = async (period = null) => {
        let url = `${BASE_URL}/goals?active_tab=0&time_range=Quarterly`;
        if (period) {
            const quarter = period.replace(" ", ""); // e.g., "2026Q1"
            url += `&quarter=${quarter}`;
        }
        try {
            const response = await axios.get(url, config);
            if (!response.data.success) throw new Error(response.data.message || "Failed to fetch goals");
            return response.data.data;
        } catch (error) {
            NotificationManager.error(error.response?.data?.message || "Failed to fetch goals");
            throw error;
        }
    };

    // Fetch goals & actuals whenever selected period changes
    useEffect(() => {
        if (!selectedPeriod) return;

        setLoading(true);
        fetchGoals(selectedPeriod)
            .then((data) => {
                const goals = data.goal || {};
                const actualsData = data;

                // Populate editable goals for the form
                const updatedGoals = {};
                Object.keys(goalMap).forEach((displayName) => {
                    const key = goalMap[displayName];
                    updatedGoals[key] = goals[key] ?? "";
                });
                updatedGoals.actual_goal = goals.actual_goal ?? "";
                // Handle special int fields if backend uses different keys
                if (goals.dials_goal_int) updatedGoals.dials_goal = goals.dials_goal_int;
                if (goals.conversations_goal_int) updatedGoals.conversations_goal = goals.conversations_goal_int;
                if (goals.talk_time_goal_int) updatedGoals.talk_time_goal = goals.talk_time_goal_int;
                if (goals.new_8x8s_goal_int) updatedGoals.new_8x8s_goal = goals.new_8x8s_goal_int;
                if (goals.new_buyer_leads_goal_int) updatedGoals.new_buyer_leads_goal = goals.new_buyer_leads_goal_int;
                if (goals.initial_offers_w_different_buyers_goal_int) updatedGoals.initial_offers_w_different_buyers_goal = goals.initial_offers_w_different_buyers_goal_int;
                if (goals.new_seller_leads_goal_int) updatedGoals.new_seller_leads_goal = goals.new_seller_leads_goal_int;
                if (goals.new_bovs_goal_int) updatedGoals.new_bovs_goal = goals.new_bovs_goal_int;
                if (goals.initial_offers_w_different_sellers_goal_int) updatedGoals.initial_offers_w_different_sellers_goal = goals.initial_offers_w_different_sellers_goal_int;
                if (goals.exclusive_listings_goal_int) updatedGoals.exclusive_listings_goal = goals.exclusive_listings_goal_int;
                if (goals.executed_loi_goal_int) updatedGoals.executed_loi_goal = goals.executed_loi_goal_int;
                if (goals.gross_hopper_gains_goal_int) updatedGoals.gross_hopper_gains_goal = goals.gross_hopper_gains_goal_int;
                if (goals.gross_commission_closed_goal_int) updatedGoals.gross_commission_closed_goal = goals.gross_commission_closed_goal_int;

                setCurrentGoals(updatedGoals);

                // Populate table data (goals + actuals)
                const left = leftGoalTypes.map((type) => ({
                    type,
                    goal: goals[goalMap[type]] ?? "",
                    actual: actualsData[actualMap[type]] ?? "",
                }));

                const right = rightGoalTypes.map((type) => ({
                    type,
                    goal: goals[goalMap[type]] ?? "",
                    actual: actualsData[actualMap[type]] ?? "",
                }));

                setHistoricalData({ left, right });
            })
            .catch((error) => console.error("Error fetching goals:", error))
            .finally(() => setLoading(false));
    }, [selectedPeriod]);

    const handleGoalChange = (key, value) => {
        const numericValue = value === "" ? "" : Math.max(0, Number(value));

        setCurrentGoals((prev) => ({
            ...prev,
            [key]: numericValue,
        }));
    };


    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                ...currentGoals,
                quarter: selectedPeriod.replace(" ", ""), // e.g., "2026Q1"
            };

            const response = await axios.post(`${BASE_URL}/goals`, payload, config);
            if (!response.data.success) throw new Error(response.data.message || "Failed to update goals");

            NotificationManager.success(`Goals updated successfully for ${selectedPeriod}!`);
        } catch (error) {
            NotificationManager.error(error.response?.data?.message || "Failed to update goals. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BaseLayout isPadding={false}>
            <div className="p-6">
                <h2 className="head-1 dark-H mb-8">Add Scorecard Goals</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                    {/* Editable Goals for Selected Quarter */}
                    <div className="bg-white p-6 h-[850px] overflow-y-auto">
                        <h3 className="dark-H head-4 mb-6">
                            Goals for <span className="text-green-600">{selectedPeriod || "Loading..."}</span>
                        </h3>

                        <div className="space-y-5">
                            {leftGoals.map((goal) => (
                                <div key={goal} className="flex items-center justify-between gap-4">
                                    <span className="text-base dark-H body-N max-w-[200px] break-words leading-snug">
                                        {goal}
                                    </span>

                                    {moneyFields.includes(goal) ? (
                                        <div className="flex items-center">
                                            <span className="mr-1">$</span>
                                            <input
                                                type="number"
                                                min={0}
                                                className="border px-4 py-2 w-24 head-5 bg-gray-50 text-center shrink-0"
                                                value={currentGoals[goalMap[goal]] ?? ""}
                                                onChange={(e) => handleGoalChange(goalMap[goal], e.target.value)}
                                            />

                                        </div>
                                    ) : (
                                        <input
                                            type="number"
                                            min={0}
                                            className="border px-4 py-2 w-24 head-5 bg-gray-50 text-center shrink-0"
                                            value={currentGoals[goalMap[goal]] ?? ""}
                                            onChange={(e) => handleGoalChange(goalMap[goal], e.target.value)}
                                        />

                                    )}
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={handleSave}
                            disabled={saving || loading}
                            className="mt-8 save-button light-L head-5 green-bg-H w-full"
                        >
                            {saving ? "Saving..." : "Save Goals"}
                        </button>
                    </div>

                    {/* Historical / Selected Quarter View */}
                    <div className="md:col-span-2 bg-white p-6">
                        <h3 className="dark-H head-4">Goal vs Actual Performance</h3>

                        {/* Period Selector */}
                        <div className="flex flex-wrap gap-3 mt-6 mb-6">
                            {periods.map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setSelectedPeriod(p)}
                                    disabled={loading}
                                    className={`px-4 py-2 border head-5 rounded transition ${selectedPeriod === p
                                        ? "bg-green-200 border-green-600 font-semibold"
                                        : "bg-white hover:bg-gray-100"
                                        } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>

                        {loading ? (
                            <div className="text-center py-8 text-gray-500">Loading data for {selectedPeriod}...</div>
                        ) : (
                            <div className="border rounded-2xl p-6 bg-white overflow-x-auto">
                                <table className="w-full text-sm min-w-[600px]">
                                    <thead>
                                        <tr className="font-semibold text-gray-700 text-left border-b">
                                            <th className="py-2 whitespace-nowrap">Goal Type</th>
                                            <th className="py-2 whitespace-nowrap">Goal</th>
                                            <th className="py-2 whitespace-nowrap">Actual</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...historicalData.left, ...historicalData.right].map((row) => (
                                            <tr key={row.type} className="border-b last:border-none">
                                                <td className="py-3 whitespace-normal break-words max-w-[180px]">
                                                    {formatType(row.type)}
                                                </td>
                                                <td>
                                                    {moneyFields.includes(row.type)
                                                        ? row.goal !== "" ? `$${row.goal}` : "-"
                                                        : row.goal || "-"}
                                                </td>
                                                <td>
                                                    {moneyFields.includes(row.type)
                                                        ? row.actual !== "" ? `$${row.actual}` : "-"
                                                        : row.actual || "-"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </BaseLayout>
    );
};

export default ScoreCardHistory;
